import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { site } from "../data/site";
import { projects } from "../data/projects";
import { services } from "../data/services";
import Button from "../components/Button";
import { IconArrow, IconArrowDown } from "../components/icons";
import {
  BlueprintGrid,
  ContourLines,
  CompassRose,
  ScaleBar,
  DimensionLine,
} from "../components/Glyphs";
import useReveal from "../components/useReveal";
import styles from "./Home.module.css";

export default function Home() {
  useReveal();

  // Parallax for the hero image — translate it slightly slower than
  // the page scroll so the building drifts upward as you scroll past
  // the fold. Caps the translate so it doesn't ever leave the frame
  // on a really long scroll.
  const heroImgRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const el = heroImgRef.current;
      if (!el) return;
      const y = Math.min(window.scrollY, 800);
      el.style.transform = `translateY(${y * -0.18}px) scale(1.06)`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const featured = projects.slice(0, 3);
  const more = projects.slice(3);

  return (
    <>
      {/* HERO — full-bleed photograph with a slow vertical parallax,
          a draw-on axonometric overlay, and a typeset "drawing block"
          in the lower-left corner. */}
      <section className={styles.hero}>
        <div className={styles.heroImageWrap}>
          <img
            ref={heroImgRef}
            src="/photos/guesthouse-hero.jpg"
            alt=""
            className={styles.heroImg}
            loading="eager"
            fetchpriority="high"
          />
          <div className={styles.heroOverlay} aria-hidden="true" />
        </div>

        <div className={styles.heroInner}>
          <div className={styles.heroMeta}>
            <span className={styles.heroMetaItem}>{site.city}</span>
            <span className={styles.heroMetaDot} />
            <span className={styles.heroMetaItem}>Est. {site.founded}</span>
            <span className={styles.heroMetaDot} />
            <span className={styles.heroMetaItem}>AIA · MT</span>
          </div>
          <h1 className={styles.heroTitle}>
            <span className={styles.heroTitleLine}>Thoughtful</span>
            <span className={styles.heroTitleLine}>design.</span>
            <span className={[styles.heroTitleLine, styles.heroTitleAccent].join(" ")}>
              Lasting impact.
            </span>
          </h1>
          <p className={styles.heroLede}>
            UPSHOT is a Bozeman, Montana studio designing contemporary residential
            and commercial architecture — built to outlast the trend cycle and
            settle into its site.
          </p>
          <div className={styles.heroCtas}>
            <Button to="/projects" variant="brass" size="lg">
              See the Work <IconArrow />
            </Button>
            <Button to="/contact" variant="ghost" size="lg">
              Start a Project
            </Button>
          </div>

          <div className={styles.heroDrawingBlock}>
            <div className={styles.heroDbHead}>
              <span>UPSHOT/01</span>
              <span>SHEET A0.0</span>
            </div>
            <div className={styles.heroDbBody}>
              <span>SUBJECT</span>
              <span>BOZEMAN GUESTHOUSE</span>
            </div>
            <div className={styles.heroDbBody}>
              <span>VIEW</span>
              <span>WEST ELEVATION</span>
            </div>
            <ScaleBar width={200} color="#f5f1e8" />
          </div>
        </div>

        <a href="#intro" className={styles.scrollCue} aria-label="Scroll">
          <IconArrowDown />
        </a>
      </section>

      {/* MARQUEE — restless ticker. Drawing-set vocabulary. */}
      <div className={styles.marquee} role="presentation">
        <div className={styles.marqueeTrack}>
          {[...Array(2)].map((_, i) => (
            <div className={styles.marqueeRow} key={i}>
              <span>RESIDENTIAL</span>
              <span className={styles.marqueeDot}>◆</span>
              <span>COMMERCIAL</span>
              <span className={styles.marqueeDot}>◆</span>
              <span>REMODEL · ADDITION</span>
              <span className={styles.marqueeDot}>◆</span>
              <span>SCHEMATIC TO PUNCH LIST</span>
              <span className={styles.marqueeDot}>◆</span>
              <span>BOZEMAN · MT</span>
              <span className={styles.marqueeDot}>◆</span>
              <span>EST. {site.founded}</span>
              <span className={styles.marqueeDot}>◆</span>
            </div>
          ))}
        </div>
      </div>

      {/* INTRO — short statement of what makes UPSHOT UPSHOT, with
          an animated topographic contour set behind. */}
      <section id="intro" className={styles.intro}>
        <ContourLines
          className={[styles.introContours, "reveal"].join(" ")}
          color="#1a1d20"
          width={1200}
          height={420}
        />
        <div className={styles.introInner}>
          <div className={[styles.introCopy, "reveal"].join(" ")}>
            <span className={styles.eyebrow}>The Studio</span>
            <h2 className={styles.introTitle}>
              Architecture by people who have spent time on a job site.
            </h2>
            <p className={styles.introBody}>
              {site.shortBio} We sketch with the section, detail with the trades,
              and write specs we'd be willing to build from ourselves.
            </p>
            <Link to="/about" className={styles.introLink}>
              Meet the studio <IconArrow />
            </Link>
          </div>
          <ul className={[styles.introStats, "reveal"].join(" ")}>
            <li>
              <div className={styles.statNum}>20+</div>
              <div className={styles.statLabel}>Projects in Montana</div>
            </li>
            <li>
              <div className={styles.statNum}>4</div>
              <div className={styles.statLabel}>Service phases, listen → build</div>
            </li>
            <li>
              <div className={styles.statNum}>1:1</div>
              <div className={styles.statLabel}>Principal-led, every project</div>
            </li>
          </ul>
        </div>
      </section>

      {/* FEATURED WORK — three big cards, alternating sides, with
          row indices that read as drawing-set sheet numbers. */}
      <section className={styles.work}>
        <div className={[styles.sectionHead, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>Selected Work</span>
          <div className={styles.sectionHeadRow}>
            <h2 className={styles.sectionTitle}>Buildings designed to last.</h2>
            <Link to="/projects" className={styles.headLink}>
              All work <IconArrow />
            </Link>
          </div>
        </div>

        <div className={styles.workList}>
          {featured.map((p, i) => (
            <article
              key={p.id}
              className={[
                styles.workItem,
                i % 2 ? styles.workRight : styles.workLeft,
                "reveal",
              ].join(" ")}
            >
              <div className={styles.workMeta}>
                <span className={styles.workIndex}>
                  A{(i + 1).toString().padStart(2, "0")}
                </span>
                <span className={styles.workType}>{p.type}</span>
              </div>
              <div className={styles.workImageWrap}>
                <Link to="/projects" className={styles.workImage}>
                  <img src={p.photo} alt={p.photoAlt} loading="lazy" />
                  <span className={styles.workImageHover}>View Work →</span>
                </Link>
              </div>
              <div className={styles.workCopy}>
                <h3 className={styles.workTitle}>{p.name}</h3>
                <div className={styles.workSpecs}>
                  <span>{p.location}</span>
                  <span>·</span>
                  <span>{p.year}</span>
                </div>
                <p className={styles.workBlurb}>{p.blurb}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* DIMENSION DIVIDER */}
      <div className={styles.dimDivider}>
        <DimensionLine color="#1a1d20" label="UPSHOT · SERVICES · 04 PHASES" />
      </div>

      {/* SERVICES — 3 columns, with a drawing-block style frame. */}
      <section className={styles.services}>
        <BlueprintGrid color="#1a1d20" opacity={0.05} step={32} />
        <div className={styles.servicesInner}>
          <div className={[styles.sectionHead, "reveal"].join(" ")}>
            <span className={styles.eyebrow}>What We Do</span>
            <h2 className={styles.sectionTitle}>
              Three studios under one roof.
            </h2>
          </div>

          <div className={styles.serviceGrid}>
            {services.map((s) => (
              <article
                key={s.id}
                className={[styles.serviceCard, "reveal"].join(" ")}
              >
                <div className={styles.serviceHead}>
                  <span className={styles.serviceCode}>{s.code}</span>
                  <h3 className={styles.serviceName}>{s.name}</h3>
                </div>
                <p className={styles.serviceSummary}>{s.summary}</p>
                <ul className={styles.serviceList}>
                  {s.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className={styles.servicesFoot}>
            <Link to="/services" className={styles.headLink}>
              How we work <IconArrow />
            </Link>
          </div>
        </div>
      </section>

      {/* MORE WORK — small grid of three remaining projects. */}
      <section className={styles.moreWork}>
        <div className={[styles.sectionHead, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>From the Archive</span>
          <h2 className={styles.sectionTitle}>More from the studio.</h2>
        </div>
        <div className={styles.moreGrid}>
          {more.map((p) => (
            <Link
              to="/projects"
              key={p.id}
              className={[styles.moreCard, "reveal"].join(" ")}
            >
              <div className={styles.moreImg}>
                <img src={p.photo} alt={p.photoAlt} loading="lazy" />
              </div>
              <div className={styles.moreCopy}>
                <span className={styles.moreType}>{p.type}</span>
                <span className={styles.moreName}>{p.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY BAND — dark, with compass + topo. */}
      <section className={styles.philosophy}>
        <CompassRose className={styles.philosophyCompass} size={120} color="#d8c9a8" />
        <div className={[styles.philosophyInner, "reveal"].join(" ")}>
          <span className={styles.philosophyEyebrow}>Studio Philosophy</span>
          <p className={styles.philosophyQuote}>
            “A building should sit on its site like it was always going to be
            there. The work is shape, light, and the small joints that decide
            whether you'll still love it in twenty years.”
          </p>
          <div className={styles.philosophyAttr}>
            <span>—</span>
            <span>{site.principal}</span>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={styles.finalCta}>
        <div className={[styles.finalInner, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>Next Step</span>
          <h2 className={styles.finalTitle}>
            Got a site, an idea, a back-of-napkin sketch?
          </h2>
          <p className={styles.finalLede}>
            We'd love to hear what you're thinking. First conversations are free
            and there's no pitch deck — just a phone call and an honest read on
            whether we're the right fit.
          </p>
          <div className={styles.heroCtas}>
            <Button to="/contact" variant="primary" size="lg">
              Start a Project <IconArrow />
            </Button>
            <Button href={`tel:${site.phoneTel}`} variant="outline" size="lg">
              {site.phone}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
